// Pure aggregation of Respite records, shared with apps/web through respiteMetrics.ts. Every
// count keeps the carespace ids behind it, so the page can link any number to its CareSpaces.
import type {
  RespiteCarespaceRef,
  RespiteEligibleRecord,
  RespiteHsaBucket,
  RespiteHsaRecord,
  RespiteReferralRecord,
} from "./respite.types";

const HSA_COUNT_KEYS = [
  "cohort",
  "requested",
  "completedOnTime",
  "completedLate",
  "completed",
  "openInWindow",
  "dueSoon",
  "openOverdue",
  "due",
  "declined",
  "ineligible",
  "noAnswer",
  "notRequested",
] as const;

export type RespiteHsaCountKey = (typeof HSA_COUNT_KEYS)[number];

/** Mutually exclusive HSA buckets plus the roll-ups the page shows, each with its carespaces. */
export type RespiteHsaBuckets = Record<RespiteHsaCountKey, number> & {
  /** completedOnTime / (completedOnTime + completedLate + openOverdue); null when nothing is due. */
  goalRate: number | null;
  ids: Record<RespiteHsaCountKey, string[]>;
};

const BUCKET_KEY: Record<RespiteHsaBucket, RespiteHsaCountKey> = {
  completed_on_time: "completedOnTime",
  completed_late: "completedLate",
  open_in_window: "openInWindow",
  open_overdue: "openOverdue",
  declined: "declined",
  ineligible: "ineligible",
  no_answer: "noAnswer",
};

const ROLL_UPS: Partial<Record<RespiteHsaBucket, RespiteHsaCountKey[]>> = {
  completed_on_time: ["completed", "due"],
  completed_late: ["completed", "due"],
  open_overdue: ["due"],
  declined: ["notRequested"],
  ineligible: ["notRequested"],
  no_answer: ["notRequested"],
};

function emptyIds<K extends string>(keys: readonly K[]): Record<K, string[]> {
  return Object.fromEntries(keys.map((key) => [key, []])) as unknown as Record<K, string[]>;
}

function countsOf<K extends string>(ids: Record<K, string[]>): Record<K, number> {
  return Object.fromEntries(
    Object.entries<string[]>(ids).map(([key, values]) => [key, values.length]),
  ) as Record<K, number>;
}

export function summarizeHsa(records: RespiteHsaRecord[]): RespiteHsaBuckets {
  const ids = emptyIds(HSA_COUNT_KEYS);
  for (const record of records) {
    const id = record.carespaceId;
    ids.cohort.push(id);
    if (record.requested) ids.requested.push(id);
    if (record.dueSoon) ids.dueSoon.push(id);
    ids[BUCKET_KEY[record.bucket]].push(id);
    for (const key of ROLL_UPS[record.bucket] ?? []) ids[key].push(id);
  }
  const counts = countsOf(ids);
  return { ...counts, goalRate: counts.due > 0 ? counts.completedOnTime / counts.due : null, ids };
}

/** Records whose clock started between `from` and `to` (inclusive, YYYY-MM-DD). */
export function hsaClockBetween(records: RespiteHsaRecord[], from: string, to: string) {
  return records.filter((record) => record.clockStart >= from && record.clockStart <= to);
}

function groupBy<T>(rows: T[], key: (row: T) => string): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const row of rows) {
    const value = key(row);
    const group = groups.get(value);
    if (group) group.push(row);
    else groups.set(value, [row]);
  }
  return groups;
}

export function hsaByMonth(records: RespiteHsaRecord[]) {
  return [...groupBy(records, (record) => record.clockStart.slice(0, 7))]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([month, rows]) => ({ month, ...summarizeHsa(rows) }));
}

export type RespiteProviderGroup<T> = T & {
  providerGroup: string;
  providers: Array<T & { providerName: string }>;
};

function byProviderGroup<R extends { providerGroup: string; providerName: string }, T>(
  records: R[],
  summarize: (rows: R[]) => T,
): Array<RespiteProviderGroup<T>> {
  return [...groupBy(records, (record) => record.providerGroup)].map(([providerGroup, rows]) => ({
    ...summarize(rows),
    providerGroup,
    providers: [...groupBy(rows, (record) => record.providerName)]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([providerName, providerRows]) => ({ ...summarize(providerRows), providerName })),
  }));
}

export function hsaByProviderGroup(records: RespiteHsaRecord[]) {
  return byProviderGroup(records, summarizeHsa);
}

const REFERRAL_COUNT_KEYS = [
  "total",
  "open",
  "pending",
  "inProgress",
  "completed",
  "cancelled",
  "rereferred",
  "leftProgram",
  "archived",
  "openCarePlanOnFile",
  "openCarePlanUpdated",
] as const;

export type RespiteReferralCountKey = (typeof REFERRAL_COUNT_KEYS)[number];

/**
 * Referral counts by status. `cancelled` is a lost referral: a cancellation followed by a
 * referral to another provider counts as `rereferred`, and otherwise one whose carespace is now
 * archived or not aligned counts as `leftProgram`.
 */
export type RespiteReferralCounts = Record<RespiteReferralCountKey, number> & {
  ids: Record<RespiteReferralCountKey, string[]>;
};

const STATUS_KEY = {
  pending: "pending",
  in_progress: "inProgress",
  completed: "completed",
  cancelled: "cancelled",
  archived: "archived",
} as const;

function referralBucket(record: RespiteReferralRecord): RespiteReferralCountKey {
  if (record.status !== "cancelled") return STATUS_KEY[record.status];
  if (record.rereferred) return "rereferred";
  return record.carespaceInactive ? "leftProgram" : "cancelled";
}

export function summarizeReferrals(records: RespiteReferralRecord[]): RespiteReferralCounts {
  const ids = emptyIds(REFERRAL_COUNT_KEYS);
  for (const record of records) {
    const id = record.carespaceId;
    ids.total.push(id);
    ids[referralBucket(record)].push(id);
    if (record.status === "pending" || record.status === "in_progress") {
      ids.open.push(id);
      if (record.carePlanOnFile) ids.openCarePlanOnFile.push(id);
      if (record.carePlanUpdatedSinceReferral) ids.openCarePlanUpdated.push(id);
    }
  }
  return { ...countsOf(ids), ids };
}

export function referralsByProviderGroup(records: RespiteReferralRecord[]) {
  return byProviderGroup(records, summarizeReferrals);
}

export function eligibleByCounty(records: RespiteEligibleRecord[]) {
  return [...groupBy(records, (record) => `${record.state}\u0000${record.county}`)].map(
    ([, rows]) => ({
      state: rows[0]!.state,
      county: rows[0]!.county,
      count: rows.length,
      ids: rows.map((row) => row.carespaceId),
    }),
  );
}

// Separators and connecting words a group name should not end on ("Synergy HomeCare of").
const TRAILING_FILLER = /(?:[\s\-–—,:;(/|&]+|\s(?:of|the|and|at|in|for))+$/iu;

/** The most frequent value, ties broken alphabetically. */
function mostCommon(values: string[]): string {
  const counts = [...groupBy(values, (value) => value)].map(([value, rows]) => ({
    value,
    count: rows.length,
  }));
  counts.sort((left, right) => right.count - left.count || left.value.localeCompare(right.value));
  return counts[0]?.value ?? "";
}

/**
 * A readable name for sites that share a provider group: the longest run of opening words that
 * most site names share ("Cornerstone Caregiving Denton, TX" and "Cornerstone Caregiving Katy"
 * give "Cornerstone Caregiving", even beside a misspelled "Conerstone Caregiving Pensacola"),
 * else the shortest site name.
 */
export function providerGroupLabel(names: string[]): string {
  const unique = [...new Set(names)].sort();
  if (unique.length <= 1) return unique[0] ?? "";
  const words = unique.map((name) => name.split(/\s+/).filter(Boolean));
  const normalize = (word: string) => word.toLowerCase().replace(/[^\p{L}\p{N}]/gu, "");
  let label = "";
  for (let length = 1; ; length += 1) {
    const prefixes = groupBy(
      words.filter((name) => name.length >= length),
      (name) => name.slice(0, length).map(normalize).join(" "),
    );
    const [, best = []] =
      [...prefixes].sort(([, left], [, right]) => right.length - left.length)[0] ?? [];
    if (best.length * 2 <= unique.length) break;
    label = mostCommon(best.map((name) => name.slice(0, length).join(" ")));
  }
  label = label.replace(TRAILING_FILLER, "").trim();
  return (
    label || unique.reduce((shortest, name) => (name.length < shortest.length ? name : shortest))
  );
}

/**
 * Words for brand matching: lowercase, punctuation dropped, plurals trimmed and "home care"
 * joined, so "SYNERGY Home Care" and "Synergy HomeCare" and "Seniors Helpers" and "Senior Helpers"
 * agree.
 */
function brandWords(name: string): string[] {
  const words: string[] = [];
  for (const word of name.toLowerCase().split(/[^\p{L}\p{N}]+/u)) {
    if (!word) continue;
    const previous = words.at(-1);
    if (word === "care" && (previous === "home" || previous === "health" || previous === "day")) {
      words[words.length - 1] = `${previous}care`;
    } else {
      words.push(word.length > 3 && word.endsWith("s") ? word.slice(0, -1) : word);
    }
  }
  return words;
}

// The second brand word matches on its opening letters: "Caregiving", "Caregivers".
const SECOND_WORD_LETTERS = 5;

/** The first brand word and the second's opening letters that most of a group's sites share. */
function brandKey(names: string[]): string[] {
  const words = names.map(brandWords);
  const key: string[] = [];
  for (let index = 0; index < 2; index += 1) {
    const present = words.flatMap((name) => {
      const word = name[index];
      if (word === undefined) return [];
      return [index === 0 ? word : word.slice(0, SECOND_WORD_LETTERS)];
    });
    const word = mostCommon(present);
    const count = present.filter((value) => value === word).length;
    if (!word || (words.length > 1 && count * 2 <= words.length)) break;
    key.push(word);
  }
  return key;
}

function oneEditApart(left: string, right: string): boolean {
  if (left === right) return true;
  if (Math.min(left.length, right.length) < 6 || Math.abs(left.length - right.length) > 1) {
    return false;
  }
  let index = 0;
  while (index < left.length && left[index] === right[index]) index += 1;
  return (
    left.slice(index + 1) === right.slice(index + 1) ||
    left.slice(index + 1) === right.slice(index) ||
    left.slice(index) === right.slice(index + 1)
  );
}

/**
 * Same brand: the first words match allowing one typo ("Conerstone") and the second words' opening
 * letters match. Two words are required, so "Senior Helpers" and "Seniors Helping Seniors" stay
 * apart.
 */
function sameBrand(left: string[], right: string[]): boolean {
  return (
    left.length === 2 &&
    right.length === 2 &&
    oneEditApart(left[0]!, right[0]!) &&
    left[1] === right[1]
  );
}

/**
 * Provider name to group label, from (provider name, group key) pairs. Sites that share a group
 * key (a Pylon account: one business) start together; groups whose names show the same brand then
 * merge, since every franchise location ("Synergy HomeCare of Tucson") has its own account.
 */
export function providerGroupLabels(
  providers: Array<{ providerName: string; groupKey: string }>,
): Map<string, string> {
  const accounts = [...groupBy(providers, (row) => row.groupKey).values()].map((rows, index) => {
    const names = rows.map((row) => row.providerName);
    return { index, names, key: brandKey(names) };
  });
  const parent = accounts.map((account) => account.index);
  const root = (index: number): number => {
    let current = index;
    while (parent[current] !== current) current = parent[current]!;
    return current;
  };
  for (let left = 0; left < accounts.length; left += 1) {
    for (let right = left + 1; right < accounts.length; right += 1) {
      if (sameBrand(accounts[left]!.key, accounts[right]!.key)) parent[root(left)] = root(right);
    }
  }
  const labels = new Map<string, string>();
  for (const brand of groupBy(accounts, (account) => String(root(account.index))).values()) {
    const names = brand.flatMap((account) => account.names);
    const label = providerGroupLabel(names);
    for (const name of names) labels.set(name, label);
  }
  return labels;
}

/** Distinct carespaces across record lists, for the drill-down CSV. */
export function carespaceRefs(...lists: RespiteCarespaceRef[][]): RespiteCarespaceRef[] {
  const refs = new Map<string, RespiteCarespaceRef>();
  for (const list of lists) {
    for (const ref of list) {
      if (!refs.has(ref.carespaceId)) {
        refs.set(ref.carespaceId, {
          carespaceId: ref.carespaceId,
          clinicName: ref.clinicName,
          navigatorName: ref.navigatorName,
        });
      }
    }
  }
  return [...refs.values()];
}
